import type { Charges } from '@/v2/services/availability/types/api/parsedResponse';
import React, { useMemo, useState } from 'react';
import { Box, Container, Heading } from '@qga/roo-ui/components';

import useSearchQuery from '@shared/v2/hooks/useSearchQuery';
import { Flights } from '@shared/v2/services/types/api/parsedResponse';

import FlightsTerms from '@/components/FlightsTerms';
import FullPageModal from '@/components/FullPageModal';
import { useFlightModal } from '@/v2/components/pages/PackageView/contexts/FlightModalContext';
import useFlightOptionInteractionsEvent from '@/v2/hooks/useFlightOptionInteractionsEvent';
import useFlightViewItemListEvent from '@/v2/hooks/useFlightViewItemListEvent';
import FlightSummary from '../components/FlightSummary';
import { getCheapestReturnFlightJourney } from '../utils/getCheapestReturnFlightJourney';
import { getReturnOfferCharges } from '../utils/getOfferCharges';
import { ReturnFlightDateDisplay } from './components/ReturnFlightDateDisplay';
import {
  ReturnFlightList,
  ReturnFlightListProps,
} from './components/ReturnFlightList';

interface ReturnFlightOptionsProps {
  flightOptions: ReturnFlightListProps['flightOptions'];
  charges: Charges | undefined;
  onConfirmFlights: (segments: { segments: Flights }) => void;
  offerId?: string;
}

export const ReturnFlightOptions = ({
  flightOptions,
  charges,
  onConfirmFlights,
  offerId,
}: ReturnFlightOptionsProps) => {
  const { searchQuery } = useSearchQuery();

  const [selectedOption, setSelectedOption] = useState<
    ReturnFlightListProps['selectedFlightOption'] | undefined
  >(getCheapestReturnFlightJourney(flightOptions));
  useFlightViewItemListEvent({
    flightOptions: flightOptions,
  });
  const { handleInvokeFlightOptionInteractionsEvent } =
    useFlightOptionInteractionsEvent({
      availablePackageCount: flightOptions.length,
    });
  const { closeFlightModal } = useFlightModal();

  const offerCharges = useMemo(() => {
    return (
      charges &&
      selectedOption &&
      getReturnOfferCharges({
        offer: { charges: charges },
        selectedOption,
      })
    );
  }, [charges, selectedOption]);

  const handleClose = () => closeFlightModal();

  const onDone = () => {
    if (selectedOption && offerCharges) {
      handleInvokeFlightOptionInteractionsEvent({
        flightOption: selectedOption,
        packagePrice: Number(offerCharges.total.amount),
        action: 'add',
      });
      onConfirmFlights({ segments: selectedOption.flightSegments });
    }
  };

  const firstOutbound = flightOptions[0].flightSegments.outbound;
  const firstInbound = flightOptions[0].flightSegments.inbound;

  const { origin } = firstOutbound[0];
  const { origin: destination } = firstInbound[0];
  const routeText = `Return flights ${origin.name} (${origin.code}) to ${destination.name} (${destination.code})`;

  const outboundDate = firstOutbound[0].departure.date;
  const inboundDate = firstInbound[0].departure.date;
  const departureDate = searchQuery?.departureDate ?? outboundDate;
  const returnDate = searchQuery?.returnDate ?? inboundDate;

  return (
    <FullPageModal onClose={handleClose}>
      <FullPageModal.Header>
        <Heading.h1 textStyle="h5" mb="0">
          Flights selection
        </Heading.h1>
      </FullPageModal.Header>
      {/* @ts-expect-error */}
      <FullPageModal.Body bg="greys.porcelain">
        <Box
          position="sticky"
          top="0"
          left="0"
          right="0"
          bg="white"
          borderBottom="1px solid"
          borderColor="greys.alto"
          // @ts-expect-error "popup" is a valid custom z-index
          zIndex="popup"
          textAlign="center"
        >
          <Box pt="4">
            <Heading.h2 textStyle="text" fontWeight="bold" fontSize="sm" mb="0">
              {routeText}
            </Heading.h2>
          </Box>
          <Box pb="4">
            <ReturnFlightDateDisplay
              departureDate={departureDate}
              returnDate={returnDate}
            />
          </Box>
        </Box>

        <Box py={['6', null, '6']} px={1}>
          <Container>
            {offerCharges && selectedOption && (
              <ReturnFlightList
                selectedFlightOption={selectedOption}
                setSelectedFlightOption={setSelectedOption}
                flightOptions={flightOptions}
                charges={offerCharges}
                offerId={offerId}
              />
            )}
            <Box mt="4">
              <FlightsTerms />
            </Box>
          </Container>
        </Box>
        {offerCharges && (
          <FlightSummary
            onDone={onDone}
            charges={offerCharges}
            offerId={offerId}
          />
        )}
      </FullPageModal.Body>
    </FullPageModal>
  );
};
